import React, { useState, useEffect } from "react";
import { Form, Input, Button, Layout, Typography, message } from "antd";
import { UserOutlined, LockOutlined, LoginOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import "../styles/LoginPage.css";
import { loginRequest, verifyOtp } from "src/redux/actions/authAction";
import FloatingInput from "src/components/common/FloatingInput";

const { Title } = Typography;
const { Content } = Layout;

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [enteredOtp, setEnteredOtp] = useState<string[]>(new Array(6).fill(""));
  const [isOtpVisible, setOtpVisible] = useState<boolean>(false);
  const [isLoginButtonDisabled, setLoginButtonDisabled] =
    useState<boolean>(true);
  const themeColor = "rgba(244,229,250,1)";

  const auth = useSelector((state: any) => state.auth);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("loginPageOpen")) {
      message.info(
        "Login page is already open in another tab. Please focus that tab.",
      );
      navigate("/");
    } else {
      sessionStorage.setItem("loginPageOpen", "true");
    }
    return () => {
      sessionStorage.removeItem("loginPageOpen");
    };
  }, [navigate]);

  useEffect(() => {
    if (auth.isOtpVerified) {
      sessionStorage.setItem("isLoggedIn", "true");
      message.success("OTP verified successfully!");
      // navigate("/dashboard/home");
      window.open(`${window.location.origin}/dashboard/home`, "_self");
    } else if (auth.isAuthenticated && !isOtpVisible) {
      setOtpVisible(true);
      message.info("Please enter the 6-digit OTP sent to your email.");
    } else if (auth.error) {
      message.error(auth.error);
      if (isOtpVisible) {
        setEnteredOtp(new Array(6).fill(""));
      }
    }
  }, [
    auth.isAuthenticated,
    auth.isOtpVerified,
    auth.error,
    isOtpVisible,
    navigate,
  ]);

  const onFieldsChange = () => {
    const { username, password } = form.getFieldsValue();
    setLoginButtonDisabled(!(username && password));
  };

  const onLoginClick = (values: any) => {
    setLoading(true);
    const { username, password } = values;
    dispatch(loginRequest({ username, password }));
  };

  const onOtpClick = () => {
    const otpInput = enteredOtp.join("");
    if (otpInput.length === 6) {
      dispatch(
        verifyOtp({ username: form.getFieldValue("username"), otp: otpInput }),
      );
    } else {
      message.error("OTP must be exactly 6 digits!");
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: themeColor }}>
      <Content
        className="login-content"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: themeColor,
        }}
      >
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontFamily: "Poppins, sans-serif",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "400px",
              padding: "36px",
              backgroundColor: "#ffffff8a",
              borderRadius: "20px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              border: `2px solid ${themeColor}`,
              transition: "all 0.3s ease",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: "30px",
                fontWeight: "bolder",
                letterSpacing: "1px",
                color: "#000",
              }}
            >
              <LoginOutlined
                style={{
                  fontSize: "28px",
                  marginBottom: "10px",
                  color: "rgb(136, 66, 153)",
                }}
              />
              <h1
                id="sign_in"
                style={{ margin: 0, color: "rgb(136, 66, 153)" }}
              >
                Sign in
              </h1>
            </div>

            <Form form={form} layout="vertical" onFieldsChange={onFieldsChange}>
              <Form.Item name="username">
                <FloatingInput label="Username" icon={<UserOutlined />} />
              </Form.Item>
              <Form.Item name="password">
                <FloatingInput
                  label="Password"
                  icon={<LockOutlined />}
                  type="password"
                />
              </Form.Item>

              {!isOtpVisible && (
                <Form.Item>
                  <Button
                    type="primary"
                    block
                    size="large"
                    onClick={() => onLoginClick(form.getFieldsValue())}
                    // loading={loading}
                    // disabled={loading}
                    style={{
                      backgroundColor: "rgb(136, 66, 153)",
                      borderColor: "rgb(136, 66, 153)",
                      color: "#fff",
                      borderRadius: "10px",
                      boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                      fontWeight: 600,
                    }}
                  >
                    Login
                  </Button>
                </Form.Item>
              )}

              {isOtpVisible && (
                <>
                  <Form.Item>
                    <Input
                      value={enteredOtp.join("")}
                      maxLength={6}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setEnteredOtp(value.split("").slice(0, 6));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onOtpClick();
                      }}
                      placeholder="Enter 6-digit OTP"
                      style={{
                        width: "100%",
                        textAlign: "center",
                        fontSize: "20px",
                        height: "55px",
                        border: `1.5px solid ${themeColor}`,
                        borderRadius: "10px",
                        boxShadow: `0 0 6px ${themeColor}`,
                        backgroundColor: "#fff",
                        outline: "none",
                        transition: "all 0.2s ease",
                      }}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      type="tel"
                      aria-label="OTP input"
                    />
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      block
                      size="large"
                      onClick={onOtpClick}
                      loading={auth.loading}
                      style={{
                        backgroundColor: "rgb(136, 66, 153)",
                        borderColor: "rgb(136, 66, 153)",
                        color: "#fff",
                        borderRadius: "10px",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
                        fontWeight: 600,
                      }}
                    >
                      Verify OTP
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form>
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default LoginPage;
